import { useState, useEffect, useRef, useCallback } from 'react'
import { C, font } from '../design'
import { apiFetch } from '../utils/api'

const BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001'

function useIsMobile(bp = 640) {
  const [m, setM] = useState(() => window.innerWidth < bp)
  useEffect(() => {
    const h = () => setM(window.innerWidth < bp)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [bp])
  return m
}

// ── Stat definitions ──────────────────────────────────────────────────────────
const PERSONAL_STATS = [
  { key: 'openness',          label: 'Открытость' },
  { key: 'conscientiousness', label: 'Добросовест.' },
  { key: 'extraversion',      label: 'Экстраверсия' },
  { key: 'agreeableness',     label: 'Доброжелат.' },
  { key: 'stability',         label: 'Стабильность' },
  { key: 'communication',     label: 'Коммуникация' },
  { key: 'leadership',        label: 'Лидерство' },
  { key: 'confidence',        label: 'Уверенность' },
]

const PROFESSIONAL_STATS = [
  { key: 'technical_depth',    label: 'Тех. глубина' },
  { key: 'domain_expertise',   label: 'Экспертиза' },
  { key: 'strategic_thinking', label: 'Стратегия' },
  { key: 'project_management', label: 'Упр. проектами' },
  { key: 'market_value',       label: 'Рын. ценность' },
]

// ── Avatar effects by mastered skill count ────────────────────────────────────
const EFFECTS = [
  { id: 'glow',      threshold: 1,  label: 'Свечение' },
  { id: 'ring1',     threshold: 3,  label: 'Орбита I' },
  { id: 'ring2',     threshold: 5,  label: 'Орбита II' },
  { id: 'thirdEye',  threshold: 6,  label: 'Третий глаз' },
  { id: 'geometry',  threshold: 9,  label: 'Сакральная геометрия' },
  { id: 'crown',     threshold: 12, label: 'Корона' },
  { id: 'aura',      threshold: 16, label: 'Аура' },
]

// Which stage corresponds to which mastered count (mirrors backend)
const STAGE_THRESHOLDS = [0, 1, 3, 6, 9, 12, 16]
function getDesiredStage(count) {
  let stage = 0
  STAGE_THRESHOLDS.forEach((t, i) => { if (count >= t) stage = i })
  return stage
}
const STAGE_LABELS = [
  'Линеарт', 'Уверенность', 'Огонь в глазах',
  'Мистический воин', 'Сверхъестественное',
  'Полубог восходит', 'Вершитель судеб',
]

// ── CSS animations (injected once) ───────────────────────────────────────────
const STYLE_ID = 'dev-header-styles'
function injectStyles() {
  if (document.getElementById(STYLE_ID)) return
  const s = document.createElement('style')
  s.id = STYLE_ID
  s.textContent = `
    @keyframes spinCW  { to { transform: rotate(360deg) } }
    @keyframes spinCCW { to { transform: rotate(-360deg) } }
    @keyframes pulseAura {
      0%, 100% { opacity: 0.2; transform: scale(1); }
      50%       { opacity: 0.55; transform: scale(1.06); }
    }
    @keyframes thirdEyePulse {
      0%, 100% { opacity: 0.7; filter: drop-shadow(0 0 3px rgba(200,169,110,0.6)); }
      50%       { opacity: 1;   filter: drop-shadow(0 0 8px rgba(200,169,110,1)); }
    }
    @keyframes bonusSlide {
      from { width: 0; opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes crownPulse {
      0%, 100% { opacity: 0.5; }
      50%       { opacity: 0.9; filter: drop-shadow(0 0 6px rgba(200,169,110,0.8)); }
    }
    @keyframes statBarFadeIn {
      from { width: 0; }
    }
    .avatar-ring1 { animation: spinCW  22s linear infinite; transform-box: fill-box; transform-origin: center; }
    .avatar-ring2 { animation: spinCCW 16s linear infinite; transform-box: fill-box; transform-origin: center; }
    .avatar-third-eye { animation: thirdEyePulse 3s ease-in-out infinite; }
    .avatar-geometry  { animation: spinCW 70s linear infinite; transform-box: fill-box; transform-origin: center; opacity: 0.12; }
    .avatar-crown     { animation: crownPulse 4s ease-in-out infinite; }
    .avatar-aura      { animation: pulseAura 3s ease-in-out infinite; }
  `
  document.head.appendChild(s)
}

// ── Stat Bar ──────────────────────────────────────────────────────────────────
function StatBar({ label, base, bonus, animated }) {
  const total = Math.min(100, base + bonus)
  return (
    <div style={{ marginBottom: 7 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
        <span style={{ fontFamily: font.mono, fontSize: 8.5, color: C.silverLo, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {label}
        </span>
        <span style={{ fontFamily: font.mono, fontSize: 8.5, color: bonus > 0 ? 'rgba(100,220,150,0.9)' : C.silverLo }}>
          {base}{bonus > 0 && <span style={{ color: 'rgba(100,220,150,0.95)', fontWeight: 600 }}> +{bonus}</span>}
        </span>
      </div>
      <div style={{ position: 'relative', height: 4, borderRadius: 2, background: 'rgba(180,185,230,0.1)', overflow: 'hidden' }}>
        {/* Base bar — white/silver */}
        <div style={{
          position: 'absolute', left: 0, top: 0, height: '100%',
          width: `${base}%`,
          background: 'linear-gradient(90deg, rgba(180,185,230,0.5) 0%, rgba(220,224,240,0.85) 100%)',
          borderRadius: 2,
          animation: animated ? 'statBarFadeIn 0.9s ease-out' : 'none',
          transition: 'width 1s ease-out',
        }} />
        {/* Bonus bar — green glow */}
        {bonus > 0 && (
          <div style={{
            position: 'absolute', left: `${base}%`, top: -1,
            height: 5, borderRadius: 2,
            width: `${bonus}%`,
            background: 'linear-gradient(90deg, rgba(80,200,120,0.8), rgba(100,220,150,1))',
            boxShadow: '0 0 6px rgba(100,220,150,0.9), 0 0 14px rgba(100,220,150,0.5)',
            animation: 'bonusSlide 1.2s ease-out',
          }} />
        )}
      </div>
    </div>
  )
}

// ── Avatar Frame with SVG Effects ────────────────────────────────────────────
function AvatarFrame({ lineart, masteredCount, size, isOwner, onUploadClick, uploading, placeholder }) {
  const fx = EFFECTS.filter(e => masteredCount >= e.threshold).map(e => e.id)
  const hasGlow     = fx.includes('glow')
  const hasRing1    = fx.includes('ring1')
  const hasRing2    = fx.includes('ring2')
  const hasThirdEye = fx.includes('thirdEye')
  const hasGeometry = fx.includes('geometry')
  const hasCrown    = fx.includes('crown')
  const hasAura     = fx.includes('aura')

  const nextEffect = EFFECTS.find(e => masteredCount < e.threshold)

  return (
    <div style={{ position: 'relative', width: size, flexShrink: 0 }}>
      {/* Pulsing outer aura */}
      {hasAura && (
        <div className="avatar-aura" style={{
          position: 'absolute', inset: -16, borderRadius: '50%',
          background: 'radial-gradient(ellipse, rgba(200,169,110,0.25) 0%, transparent 70%)',
          pointerEvents: 'none', zIndex: 0,
        }} />
      )}

      {/* Hexagonal frame */}
      <div style={{
        position: 'relative', width: size, height: size,
        clipPath: 'polygon(50% 0%, 93% 25%, 93% 75%, 50% 100%, 7% 75%, 7% 25%)',
        background: lineart ? 'rgba(5,5,13,0.97)' : 'rgba(180,185,230,0.04)',
        overflow: 'hidden', zIndex: 1,
        boxShadow: hasGlow
          ? '0 0 28px rgba(255,255,255,0.12), 0 0 56px rgba(180,185,230,0.09)'
          : 'none',
        transition: 'box-shadow 1.2s ease',
      }}>
        {lineart ? (
          <img
            src={lineart}
            alt="Avatar"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block',
              filter: 'brightness(1.05) contrast(1.1)' }}
          />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {placeholder}
          </div>
        )}
      </div>

      {/* SVG overlay effects */}
      <svg
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible', zIndex: 2 }}
        width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      >
        {/* Ring 1 — dashed orbit */}
        {hasRing1 && (
          <circle className="avatar-ring1"
            cx={size / 2} cy={size / 2} r={size / 2 - 4}
            fill="none" stroke="rgba(180,185,230,0.28)" strokeWidth="1"
            strokeDasharray="5 10"
          />
        )}
        {/* Ring 2 — dotted inner orbit, gold tint */}
        {hasRing2 && (
          <circle className="avatar-ring2"
            cx={size / 2} cy={size / 2} r={size / 2 - 18}
            fill="none" stroke="rgba(200,169,110,0.22)" strokeWidth="1"
            strokeDasharray="2 8"
          />
        )}
        {/* Sacred geometry — star/hexagram */}
        {hasGeometry && (
          <g className="avatar-geometry">
            <polygon
              points={`${size/2},${size*0.04} ${size*0.94},${size/2} ${size/2},${size*0.96}`}
              fill="none" stroke="rgba(180,185,230,0.6)" strokeWidth="0.6"
            />
            <polygon
              points={`${size/2},${size*0.96} ${size*0.06},${size/2} ${size/2},${size*0.04}`}
              fill="none" stroke="rgba(180,185,230,0.6)" strokeWidth="0.6"
            />
          </g>
        )}
        {/* Third eye — on the forehead (~30% from top, center) */}
        {hasThirdEye && (
          <g className="avatar-third-eye" transform={`translate(${size * 0.5 - 14}, ${size * 0.26})`}>
            {/* Eye shape */}
            <path d="M0,8 Q14,-4 28,8 Q14,20 0,8 Z" fill="none" stroke="rgba(200,169,110,0.85)" strokeWidth="1.2" />
            {/* Iris */}
            <circle cx="14" cy="8" r="4.5" fill="rgba(200,169,110,0.5)" />
            <circle cx="14" cy="8" r="2" fill="rgba(200,169,110,0.9)" />
            {/* Highlight */}
            <circle cx="15.5" cy="6.5" r="0.8" fill="rgba(255,240,200,0.9)" />
            {/* Lashes */}
            <line x1="2" y1="4" x2="-1" y2="1" stroke="rgba(200,169,110,0.5)" strokeWidth="0.8" />
            <line x1="14" y1="3" x2="14" y2="-1" stroke="rgba(200,169,110,0.5)" strokeWidth="0.8" />
            <line x1="26" y1="4" x2="29" y2="1" stroke="rgba(200,169,110,0.5)" strokeWidth="0.8" />
          </g>
        )}
        {/* Crown — rays from top center */}
        {hasCrown && (
          <g className="avatar-crown" transform={`translate(${size / 2}, ${size * 0.06})`}>
            {[-35, -20, 0, 20, 35].map((deg, i) => {
              const rad = (deg - 90) * Math.PI / 180
              const len = i === 2 ? 22 : 14
              const x2 = Math.cos(rad) * len
              const y2 = Math.sin(rad) * len
              return (
                <line key={i} x1="0" y1="0" x2={x2} y2={y2}
                  stroke="rgba(200,169,110,0.75)" strokeWidth={i === 2 ? 1.5 : 1}
                  strokeLinecap="round"
                />
              )
            })}
            <circle cx="0" cy="0" r="2.5" fill="rgba(200,169,110,0.7)" />
          </g>
        )}
      </svg>

      {/* Upload button */}
      {isOwner && (
        <label style={{
          position: 'absolute', bottom: size * 0.12, right: size * 0.08,
          width: 28, height: 28, borderRadius: '50%', zIndex: 10,
          background: 'rgba(5,5,13,0.85)', border: `1px solid ${C.borderHi}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: uploading ? 'wait' : 'pointer',
          transition: 'all 0.2s',
          boxShadow: '0 2px 8px rgba(0,0,0,0.6)',
        }}
          title="Загрузить фото"
        >
          <input type="file" accept="image/*" style={{ display: 'none' }}
            onChange={onUploadClick} disabled={uploading}
          />
          {uploading
            ? <span style={{ fontFamily: font.mono, fontSize: 10, color: C.silverLo }}>◌</span>
            : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.silverHi} strokeWidth="2" strokeLinecap="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                <polyline points="17,8 12,3 7,8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
          }
        </label>
      )}

      {/* Next effect hint */}
      {nextEffect && masteredCount > 0 && (
        <div style={{
          position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
          whiteSpace: 'nowrap', fontFamily: font.mono, fontSize: 7.5,
          color: 'rgba(180,185,230,0.4)', letterSpacing: '0.08em', zIndex: 10,
        }}>
          ◦ {nextEffect.threshold - masteredCount} до «{nextEffect.label}»
        </div>
      )}
    </div>
  )
}

// ── Stats section (one column) ────────────────────────────────────────────────
function StatsSection({ title, stats, baseStats, bonuses, headerRight }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${C.border}`,
      }}>
        <span style={{ fontFamily: font.mono, fontSize: 8.5, color: C.silverLo,
          letterSpacing: '0.15em', textTransform: 'uppercase' }}>
          {title}
        </span>
        {headerRight}
      </div>
      {stats.map(({ key, label }) => (
        <StatBar key={key} label={label}
          base={baseStats[key] || 0}
          bonus={Math.min(bonuses[key] || 0, Math.max(0, 100 - (baseStats[key] || 0)))}
          animated
        />
      ))}
    </div>
  )
}

// ── Transformation Preview Overlay ───────────────────────────────────────────
function TransformPreview({ lineart, masteredCount, onClose }) {
  const [elapsed, setElapsed] = useState(0)
  const DURATION = 10
  const isMobile = useIsMobile()
  const sz = isMobile ? 120 : 160

  useEffect(() => {
    const t = setInterval(() => {
      setElapsed(e => {
        if (e >= DURATION - 1) { clearInterval(t); onClose(); return DURATION }
        return e + 1
      })
    }, 1000)
    return () => clearInterval(t)
  }, [onClose])

  const progress = Math.min(elapsed / DURATION, 1)

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(5,5,13,0.92)',
      backdropFilter: 'blur(12px)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      {/* Close button */}
      <button onClick={onClose} style={{
        position: 'absolute', top: 20, right: 20,
        background: 'none', border: 'none', cursor: 'pointer',
        fontFamily: font.mono, fontSize: 18, color: C.silverLo,
        lineHeight: 1,
      }}>✕</button>

      {/* Title */}
      <div style={{
        fontFamily: font.heading, fontSize: isMobile ? 26 : 36,
        fontStyle: 'italic', color: C.silverHi, marginBottom: 8, letterSpacing: '0.02em',
        textAlign: 'center',
      }}>
        Твоя трансформация
      </div>
      <div style={{
        fontFamily: font.body, fontSize: 12, color: C.textMuted,
        marginBottom: isMobile ? 28 : 36, textAlign: 'center',
      }}>
        Освой 16+ навыков — и твой аватар преобразится
      </div>

      {/* Before / After frames */}
      <div style={{
        display: 'flex', alignItems: 'center',
        gap: isMobile ? 16 : 32, flexWrap: 'wrap', justifyContent: 'center',
        marginBottom: isMobile ? 28 : 36,
      }}>
        {/* Current */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <div style={{ fontFamily: font.mono, fontSize: 9, color: C.silverLo,
            letterSpacing: '0.15em', textTransform: 'uppercase' }}>
            Сейчас
          </div>
          <AvatarFrame
            lineart={lineart}
            masteredCount={masteredCount}
            size={sz}
            isOwner={false}
            onUploadClick={() => {}}
            uploading={false}
            placeholder={null}
          />
          <div style={{ fontFamily: font.mono, fontSize: 9, color: masteredCount > 0 ? 'rgba(100,220,150,0.7)' : C.silverLo }}>
            {masteredCount > 0 ? `★ ${masteredCount} навыков` : '○ 0 навыков'}
          </div>
        </div>

        {/* Arrow */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <svg width="36" height="24" viewBox="0 0 36 24" fill="none">
            <defs>
              <linearGradient id="arrowGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="rgba(180,185,230,0.3)"/>
                <stop offset="100%" stopColor="rgba(200,169,110,0.8)"/>
              </linearGradient>
            </defs>
            <path d="M0 12 L28 12 M22 6 L30 12 L22 18"
              stroke="url(#arrowGrad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        {/* Future (all effects) */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          <div style={{ fontFamily: font.mono, fontSize: 9, color: '#c8a96e',
            letterSpacing: '0.15em', textTransform: 'uppercase' }}>
            16+ навыков
          </div>
          <div style={{ animation: 'pulseAura 3s ease-in-out infinite', borderRadius: '50%' }}>
            <AvatarFrame
              lineart={lineart}
              masteredCount={999}
              size={sz}
              isOwner={false}
              onUploadClick={() => {}}
              uploading={false}
              placeholder={null}
            />
          </div>
          <div style={{ fontFamily: font.mono, fontSize: 9, color: '#c8a96e' }}>
            ★ все эффекты
          </div>
        </div>
      </div>

      {/* Effects legend */}
      <div style={{
        display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center',
        marginBottom: isMobile ? 20 : 28, maxWidth: 480,
      }}>
        {EFFECTS.map(e => (
          <span key={e.id} style={{
            fontFamily: font.mono, fontSize: 8.5, letterSpacing: '0.06em',
            padding: '2px 8px', borderRadius: 4,
            background: masteredCount >= e.threshold
              ? 'rgba(100,220,150,0.1)' : 'rgba(200,169,110,0.08)',
            border: `1px solid ${masteredCount >= e.threshold ? 'rgba(100,220,150,0.3)' : 'rgba(200,169,110,0.2)'}`,
            color: masteredCount >= e.threshold ? 'rgba(100,220,150,0.9)' : 'rgba(200,169,110,0.6)',
          }}>
            {masteredCount >= e.threshold ? '✓' : `${e.threshold}`} {e.label}
          </span>
        ))}
      </div>

      {/* Countdown bar */}
      <div style={{ width: isMobile ? '100%' : 360, maxWidth: 360 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
          <span style={{ fontFamily: font.mono, fontSize: 8.5, color: C.silverLo }}>
            закроется через
          </span>
          <span style={{ fontFamily: font.mono, fontSize: 8.5, color: C.silverLo }}>
            {DURATION - elapsed}с
          </span>
        </div>
        <div style={{ height: 2, background: C.border, borderRadius: 1, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 1,
            width: `${progress * 100}%`,
            background: 'linear-gradient(90deg, rgba(180,185,230,0.5), rgba(200,169,110,0.8))',
            transition: 'width 1s linear',
          }} />
        </div>
      </div>
    </div>
  )
}

// ── Main DevHeader component ──────────────────────────────────────────────────
export default function DevHeader({ userId, isOwner }) {
  const isMobile = useIsMobile()
  const [lineart, setLineart]         = useState(null)
  const [uploading, setUploading]     = useState(false)
  const [uploadErr, setUploadErr]     = useState('')
  const [baseStats, setBaseStats]     = useState(null)
  const [bonuses, setBonuses]         = useState({})
  const [masteredCount, setMastered]  = useState(0)
  const [evaluating, setEvaluating]   = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [avatarStage, setAvatarStage] = useState(0)
  const [regenning, setRegenning]     = useState(false)
  const [regenMsg, setRegenMsg]       = useState('')

  const avatarSize = isMobile ? 148 : 186

  useEffect(() => { injectStyles() }, [])

  useEffect(() => {
    Promise.all([
      fetch(`${BASE}/api/dev/stats/${userId}`).then(r => r.json()).catch(() => ({})),
      fetch(`${BASE}/api/dev/progress/${userId}`).then(r => r.json()).catch(() => ({})),
      fetch(`${BASE}/api/avatar/${userId}`).then(r => r.json()).catch(() => ({})),
    ]).then(([statsRes, progressRes, avatarRes]) => {
      if (statsRes.stats) setBaseStats(statsRes.stats)
      else if (!isOwner) {
        // Auto-evaluate stats for participants who don't have them yet
        setEvaluating(true)
        apiFetch(`/api/dev/evaluate-stats/${userId}`, { method: 'POST' })
          .then(r => r.json()).then(d => { if (d.stats) setBaseStats(d.stats) })
          .catch(() => {}).finally(() => setEvaluating(false))
      }
      if (progressRes.bonuses) setBonuses(progressRes.bonuses)
      if (progressRes.mastered_skills != null) setMastered(progressRes.mastered_skills)
      if (avatarRes.lineart) setLineart(avatarRes.lineart)
      if (avatarRes.stage != null) setAvatarStage(avatarRes.stage)
    })
  }, [userId])

  const handleUpload = useCallback(async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    setUploadErr('')
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = ev => resolve(ev.target.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
      const res = await apiFetch(`/api/avatar/upload/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ photo_b64: dataUrl }),
      })
      const data = await res.json()
      if (data.error) { setUploadErr(data.error); return }
      if (data.lineart) {
        setLineart(data.lineart)
        setShowPreview(true)   // ← trigger "before/after" animation
      }
    } catch (err) {
      setUploadErr('Ошибка загрузки')
    } finally {
      setUploading(false)
    }
  }, [userId])

  const handleEvaluate = useCallback(async () => {
    setEvaluating(true)
    try {
      const res = await apiFetch(`/api/dev/evaluate-stats/${userId}`, { method: 'POST' })
      const data = await res.json()
      if (data.stats) setBaseStats(data.stats)
    } catch {
    } finally {
      setEvaluating(false)
    }
  }, [userId])

  const handleRegen = useCallback(async () => {
    setRegenning(true)
    setRegenMsg('Генерирую новый образ...')
    setUploadErr('')
    try {
      const res = await apiFetch(`/api/avatar/regen/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mastered_count: masteredCount }),
      })
      const data = await res.json()
      if (data.error) { setUploadErr(data.error); return }
      if (data.lineart) {
        setLineart(data.lineart)
        setAvatarStage(data.stage ?? 0)
        setShowPreview(true)
      }
    } catch {
      setUploadErr('Ошибка генерации')
    } finally {
      setRegenning(false)
      setRegenMsg('')
    }
  }, [userId, masteredCount])

  // Placeholder SVG person icon
  const placeholderContent = (
    <>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(180,185,230,0.2)" strokeWidth="1">
        <circle cx="12" cy="8" r="4"/>
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
      </svg>
      {isOwner && (
        <span style={{ fontFamily: font.mono, fontSize: 8, color: 'rgba(180,185,230,0.25)',
          letterSpacing: '0.1em', textAlign: 'center', lineHeight: 1.6, padding: '0 16px' }}>
          ЗАГРУЗИ ФОТО↓
        </span>
      )}
    </>
  )

  const activeEffects = EFFECTS.filter(e => masteredCount >= e.threshold)
  const hasStats = baseStats && Object.keys(baseStats).length > 0
  const desiredStage = getDesiredStage(masteredCount)
  const canRegen = lineart && isOwner && desiredStage > avatarStage

  // ── Avatar column content ─────────────────────────────────────────────────
  const avatarCol = (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: 8,
      gridRow: isMobile ? undefined : '1 / span 2',
    }}>
      <AvatarFrame
        lineart={lineart}
        masteredCount={masteredCount}
        size={avatarSize}
        isOwner={isOwner}
        onUploadClick={handleUpload}
        uploading={uploading}
        placeholder={placeholderContent}
      />

      {/* Status messages */}
      {uploadErr && (
        <div style={{ fontFamily: font.body, fontSize: 10, color: 'rgba(230,100,100,0.85)',
          textAlign: 'center', lineHeight: 1.5, maxWidth: avatarSize }}>
          {uploadErr}
        </div>
      )}
      {(uploading || regenning) && (
        <div style={{ fontFamily: font.mono, fontSize: 8.5, color: C.silverLo,
          letterSpacing: '0.08em', textAlign: 'center' }}>
          ◌ {regenning ? regenMsg : 'Создаю арт-линии...'}
        </div>
      )}

      {/* Mastered count */}
      {masteredCount > 0 && (
        <div style={{ fontFamily: font.mono, fontSize: 8.5,
          color: 'rgba(100,220,150,0.75)', letterSpacing: '0.1em' }}>
          ★ {masteredCount} навыков освоено
        </div>
      )}

      {/* Active effects chips */}
      {activeEffects.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3,
          maxWidth: avatarSize + 20, justifyContent: 'center' }}>
          {activeEffects.map(e => (
            <span key={e.id} style={{
              fontFamily: font.mono, fontSize: 7, color: 'rgba(200,169,110,0.65)',
              letterSpacing: '0.05em', padding: '1px 5px',
              border: '1px solid rgba(200,169,110,0.2)', borderRadius: 3,
            }}>★ {e.label}</span>
          ))}
        </div>
      )}

      {/* Stage label */}
      {lineart && (
        <div style={{ fontFamily: font.mono, fontSize: 7.5,
          color: 'rgba(200,169,110,0.5)', letterSpacing: '0.1em',
          textTransform: 'uppercase', textAlign: 'center' }}>
          {STAGE_LABELS[avatarStage]}
        </div>
      )}

      {/* Regen button */}
      {canRegen && !regenning && (
        <button onClick={handleRegen} style={{
          background: 'rgba(200,169,110,0.08)',
          border: '1px solid rgba(200,169,110,0.45)',
          borderRadius: 7, padding: '5px 14px', cursor: 'pointer',
          fontFamily: font.mono, fontSize: 8, color: '#c8a96e',
          letterSpacing: '0.07em', textAlign: 'center',
          animation: 'crownPulse 2.2s ease-in-out infinite',
        }}>
          ✦ {STAGE_LABELS[desiredStage]}
        </button>
      )}

      {/* Preview button */}
      {lineart && (
        <button onClick={() => setShowPreview(true)} style={{
          background: 'none', border: '1px solid rgba(180,185,230,0.12)',
          borderRadius: 6, padding: '3px 10px', cursor: 'pointer',
          fontFamily: font.mono, fontSize: 7.5, color: 'rgba(180,185,230,0.35)',
          letterSpacing: '0.07em', transition: 'all 0.2s',
        }}>
          ◈ трансформация
        </button>
      )}
    </div>
  )

  // ── Empty stats placeholder ───────────────────────────────────────────────
  const emptyStats = isOwner ? (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: isMobile ? 'center' : 'flex-start',
      justifyContent: 'center', gap: 14,
      gridColumn: isMobile ? undefined : '2 / span 2',
      padding: isMobile ? '8px 0' : '20px 0',
    }}>
      <div>
        <div style={{ fontFamily: font.heading, fontSize: isMobile ? 18 : 22,
          fontStyle: 'italic', color: C.silverHi, marginBottom: 6 }}>
          Базовые статы
        </div>
        <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted,
          maxWidth: 340, lineHeight: 1.75 }}>
          Claude оценит твои навыки и способности на шкале 0–100.
          Освоение скиллов увеличит их зелёной полоской.
        </div>
      </div>
      <button onClick={handleEvaluate} disabled={evaluating} style={{
        padding: '9px 24px', borderRadius: 8, border: `1px solid ${C.silver}`,
        background: 'transparent', color: C.silverHi, fontFamily: font.mono,
        fontSize: 11, cursor: evaluating ? 'wait' : 'pointer',
        letterSpacing: '0.05em', transition: 'all 0.2s',
      }}>
        {evaluating ? '◌ Анализирую...' : '◈ Оценить навыки'}
      </button>
    </div>
  ) : null

  return (
    <>
    {showPreview && (
      <TransformPreview
        lineart={lineart}
        masteredCount={masteredCount}
        onClose={() => setShowPreview(false)}
      />
    )}
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : `${avatarSize + 24}px 1fr 1fr`,
      gap: isMobile ? 16 : 28,
      alignItems: 'start',
      padding: isMobile ? '16px 0 20px' : '24px 0 28px',
      marginBottom: 24,
      borderBottom: `1px solid ${C.border}`,
    }}>
      {/* Col 1 — Avatar */}
      {avatarCol}

      {/* Cols 2 & 3 — Stats (or empty state) */}
      {!hasStats ? emptyStats : (
        <>
          {/* Personal stats */}
          <StatsSection
            title="Личностные"
            stats={PERSONAL_STATS}
            baseStats={baseStats}
            bonuses={bonuses}
            headerRight={isOwner && (
              <button onClick={handleEvaluate} disabled={evaluating} style={{
                fontFamily: font.mono, fontSize: 8, color: C.silverLo,
                background: 'none', border: 'none', cursor: 'pointer',
                padding: 0, letterSpacing: '0.06em',
              }}>
                {evaluating ? '◌' : '↻ пересчитать'}
              </button>
            )}
          />
          {/* Professional stats */}
          <StatsSection
            title="Профессиональные"
            stats={PROFESSIONAL_STATS}
            baseStats={baseStats}
            bonuses={bonuses}
          />
        </>
      )}
    </div>
    </>
  )
}
